import { useState, useRef } from 'react';
import { useCanvasStore } from './canvas-store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MessageCircle,
  ArrowRight,
  FolderOpen,
  Clock,
  Loader2,
} from 'lucide-react';

interface ApiTask {
  id: number;
  name: string;
  color?: string;
  brandId?: number | null;
  adAccountId?: number | null;
  parentId?: number | null;
  instructions?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiProject {
  id: number;
  name: string;
  color?: string;
  brandId?: number | null;
  adAccountId?: number | null;
  instructions?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

function relativeTime(iso: string | undefined) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function FolderSkeleton() {
  return (
    <div
      data-testid="folder-skeleton"
      style={{ background: '#F7F9FC', padding: '20px 16px' }}
    >
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div
        style={{
          width: 80,
          height: 11,
          borderRadius: 6,
          marginBottom: 10,
          background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
          backgroundSize: '200% 100%',
          animation: 'chatSkeletonShimmer 1.6s ease-in-out infinite',
        }}
      />
      <div className="flex flex-col gap-1">
        {[68, 82, 55, 74, 61].map((w, i) => (
          <div
            key={i}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              background: '#FFFFFF',
              border: '1px solid #DCDCDC',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                flexShrink: 0,
                background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
                backgroundSize: '200% 100%',
                animation: `chatSkeletonShimmer 1.6s ease-in-out ${i * 0.08}s infinite`,
              }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div
                style={{
                  width: `${w}%`,
                  height: 13,
                  borderRadius: 6,
                  background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
                  backgroundSize: '200% 100%',
                  animation: `chatSkeletonShimmer 1.6s ease-in-out ${i * 0.08}s infinite`,
                }}
              />
              {i % 2 === 0 && (
                <div
                  style={{
                    width: `${Math.round(w * 0.6)}%`,
                    height: 10,
                    borderRadius: 5,
                    background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
                    backgroundSize: '200% 100%',
                    animation: `chatSkeletonShimmer 1.6s ease-in-out ${i * 0.08 + 0.1}s infinite`,
                  }}
                />
              )}
            </div>
            <div
              style={{
                width: 36,
                height: 11,
                borderRadius: 5,
                flexShrink: 0,
                background: 'linear-gradient(90deg, #E5E7EB 25%, #F3F4F6 50%, #E5E7EB 75%)',
                backgroundSize: '200% 100%',
                animation: `chatSkeletonShimmer 1.6s ease-in-out ${i * 0.08}s infinite`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}

function TaskRow({
  task,
  onOpen,
}: {
  task: ApiTask;
  onOpen: (task: ApiTask) => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={() => onOpen(task)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="w-full flex items-center gap-3 text-left transition-colors"
      style={{
        padding: '10px 14px',
        borderRadius: '10px',
        background: hovered ? '#FFFFFF' : 'transparent',
        border: '1px solid',
        borderColor: hovered ? '#DCDCDC' : 'transparent',
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: 28, height: 28, borderRadius: 7, background: '#EAF0F7' }}
      >
        <MessageCircle style={{ width: 13, height: 13, color: '#5F6D82' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate" style={{ fontSize: 13.5, color: '#1A1A1A', lineHeight: 1.3 }}>
          {task.name}
        </div>
        {task.instructions && (
          <div className="truncate" style={{ fontSize: 12, color: '#5F6D82', lineHeight: 1.4, marginTop: 1 }}>
            {task.instructions}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 flex items-center gap-1" style={{ color: '#94A3B8' }}>
        <Clock style={{ width: 11, height: 11 }} />
        <span style={{ fontSize: 11 }}>{relativeTime(task.updatedAt)}</span>
      </div>
    </button>
  );
}

export function CanvasProjectView() {
  const { folderProjectId, switchProject, setActiveTask, sendMessage } = useCanvasStore();
  const queryClient = useQueryClient();
  const [promptValue, setPromptValue] = useState('');
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  // v6.62 — brand-first: legacy folders without a brand can't mint tasks.
  const [brandGateError, setBrandGateError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: project } = useQuery<ApiProject>({
    queryKey: ['/api/canvas/projects', folderProjectId],
    queryFn: async () => {
      if (!folderProjectId) return null as unknown as ApiProject;
      const resp = await fetch(`/api/canvas/projects/${folderProjectId}`);
      if (!resp.ok) throw new Error('Failed to fetch');
      return resp.json();
    },
    enabled: !!folderProjectId,
    staleTime: 30000,
  });

  const { data: tasks = [], refetch: refetchTasks, isLoading: tasksLoading } = useQuery<ApiTask[]>({
    queryKey: ['/api/canvas/projects', folderProjectId, 'tasks'],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${folderProjectId}/tasks`);
      if (!resp.ok) throw new Error('Failed to fetch tasks');
      return resp.json();
    },
    enabled: !!folderProjectId,
    staleTime: 10000,
  });

  const handleOpenTask = async (task: ApiTask) => {
    setActiveTask(task.id);
    await switchProject(task.id, { brandId: task.brandId, adAccountId: task.adAccountId, parentId: folderProjectId });
  };

  const handleCreateTask = async () => {
    if (!folderProjectId || !promptValue.trim() || isCreatingTask) return;
    // v6.62 — brand-first (Dan's spec): a task never starts without a brand.
    // A folder without one (legacy, or its brand was deleted) can't create
    // tasks — the folder must be re-linked to a brand first.
    if (!project?.brandId) {
      setBrandGateError('Please select which brand you would like to work with — this folder has no brand attached.');
      return;
    }
    setBrandGateError(null);
    const messageToSend = promptValue.trim();
    setIsCreatingTask(true);
    try {
      const resp = await fetch('/api/canvas/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: messageToSend.slice(0, 80),
          instructions: messageToSend,
          color: '#6366F1',
          parentId: folderProjectId,
          brandId: project?.brandId ?? undefined,
          adAccountId: project?.adAccountId ?? undefined,
        }),
      });
      if (resp.ok) {
        const newTask = await resp.json();
        setPromptValue('');
        queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', folderProjectId, 'tasks'] });
        queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
        setActiveTask(newTask.id);
        await switchProject(newTask.id, { parentId: folderProjectId });
        sendMessage(messageToSend);
      }
    } finally {
      setIsCreatingTask(false);
    }
  };

  if (!folderProjectId) return null;

  const canSubmit = promptValue.trim().length > 0 && !isCreatingTask;
  const projectColor = project?.color || '#6366F1';

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'transparent' }}>
      {/* Header */}
      <div
        className="flex-shrink-0 flex items-center gap-4"
        style={{ padding: '24px 32px 20px', borderBottom: '1px solid #DCDCDC' }}
      >
        {/* Folder icon box */}
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 42, height: 42, borderRadius: 12, background: '#F3F4F6', border: '1.5px solid #E5E7EB' }}
        >
          <FolderOpen style={{ width: 20, height: 20, color: '#9CA3AF' }} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate" style={{ fontSize: 17, color: '#1A1A1A', lineHeight: 1.3 }}>
            {project?.name || 'Project'}
          </h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span style={{ fontSize: 12, color: '#5F6D82' }}>
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </span>
            {project?.createdAt && (
              <>
                <span style={{ color: '#DCDCDC', fontSize: 10 }}>·</span>
                <span style={{ fontSize: 12, color: '#5F6D82' }}>Created {relativeTime(project.createdAt)}</span>
              </>
            )}
            {project?.updatedAt && (
              <>
                <span style={{ color: '#DCDCDC', fontSize: 10 }}>·</span>
                <span style={{ fontSize: 12, color: '#5F6D82' }}>Updated {relativeTime(project.updatedAt)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '28px 32px' }}>
        {/* Centered prompt box */}
        <div style={{ maxWidth: 640, margin: '0 auto 32px' }}>
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: '#FFFFFF',
              border: '1px solid #DCDCDC',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
          >
            <textarea
              ref={textareaRef}
              value={promptValue}
              onChange={(e) => setPromptValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleCreateTask();
                }
              }}
              placeholder={`Describe a new task for ${project?.name || 'this project'}…`}
              rows={3}
              className="w-full resize-none outline-none"
              style={{
                padding: '16px 18px 10px',
                fontSize: 14,
                color: '#1A1A1A',
                background: 'transparent',
                lineHeight: 1.6,
              }}
            />
            {brandGateError && (
              <div data-testid="folder-brand-gate-error" style={{ fontSize: 11, color: '#DC2626', padding: '0 18px 6px' }}>{brandGateError}</div>
            )}
            {/* Action row */}
            <div
              className="flex items-center justify-between"
              style={{ padding: '8px 12px 12px' }}
            >
              <span style={{ fontSize: 11.5, color: '#5F6D82' }}>
                Enter to create & send · Shift+Enter for new line
              </span>
              <button
                onClick={handleCreateTask}
                disabled={!canSubmit}
                className="flex items-center gap-1.5 rounded-xl px-3.5 py-1.5 font-medium transition-all"
                style={{
                  fontSize: 13,
                  background: canSubmit ? '#1A1A1A' : '#DCE3ED',
                  color: canSubmit ? '#FFFFFF' : '#94A3B8',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {isCreatingTask ? (
                  <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />
                ) : (
                  <ArrowRight style={{ width: 13, height: 13 }} />
                )}
                {isCreatingTask ? 'Creating…' : 'New task'}
              </button>
            </div>
          </div>
        </div>

        {/* Task list */}
        {tasksLoading ? (
          <FolderSkeleton />
        ) : tasks.length > 0 ? (
          <div style={{ maxWidth: 640, margin: '0 auto' }}>
            <p style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
              Tasks · {tasks.length}
            </p>
            <div className="flex flex-col gap-0.5">
              {tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onOpen={handleOpenTask}
                />
              ))}
            </div>
          </div>
        ) : <p data-testid="project-no-tasks" style={{ maxWidth: 640, margin: '0 auto', fontSize: 13, lineHeight: 1.6, color: '#5F6D82' }}>No tasks yet. Describe what you want above, then choose New task to create a task and send your brief.</p>}
      </div>
    </div>
  );
}
